import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Typography, Button, Paper, Chip, CircularProgress, Alert, Rating, Divider } from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadIcon from "@mui/icons-material/Download";
import SurveyIcon from "@mui/icons-material/Poll";
import { QRCodeCanvas } from "qrcode.react";
import api from "../../utils/api";
import useCompanyLogo from "../../utils/useCompanyLogo";

const ViolationQRSurveyTab = ({ violationId }) => {
  const logoUrl = useCompanyLogo();
  const qrCanvasRef = useRef(null);
  const [qrData,    setQrData]    = useState(null);
  const [surveys,   setSurveys]   = useState([]);
  const [loadingQR, setLoadingQR] = useState(true);
  const [loadingSv, setLoadingSv] = useState(true);
  const [genQR,     setGenQR]     = useState(false);
  const [sendSv,    setSendSv]    = useState(false);
  const [msg,       setMsg]       = useState("");

  const loadQR = useCallback(async () => {
    setLoadingQR(true);
    try { const r = await api.get(`/violations/${violationId}/qr-code`); setQrData(r.data); }
    catch {} finally { setLoadingQR(false); }
  }, [violationId]);

  const loadSurveys = useCallback(async () => {
    setLoadingSv(true);
    try { const r = await api.get(`/violations/${violationId}/survey`); setSurveys(r.data||[]); }
    catch {} finally { setLoadingSv(false); }
  }, [violationId]);

  useEffect(() => { loadQR(); loadSurveys(); }, [loadQR, loadSurveys]);

  const handleGenQR = async () => {
    setGenQR(true);
    try {
      const r = await api.post(`/violations/${violationId}/qr-code`);
      setMsg(r.data.existing ? "QR code already exists." : "QR code generated.");
      loadQR();
    } catch (err) { setMsg(err.response?.data?.message || "Failed to generate QR code."); }
    finally { setGenQR(false); }
  };

  const handleSendSurvey = async () => {
    setSendSv(true);
    try {
      const r = await api.post(`/violations/${violationId}/send-survey`);
      setMsg(`Survey created. Share this link with the member: ${r.data.survey_url}`);
      loadSurveys();
    } catch (err) { setMsg(err.response?.data?.message || "Failed. Survey may be disabled in settings."); }
    finally { setSendSv(false); }
  };

  const qrUrl = qrData?.token
    ? `${window.location.origin}/api/violation-qr/${qrData.token}`
    : null;

  const downloadQR = () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `Violation_${violationId}_QRCode.png`;
    link.click();
  };

  const avgRating = surveys.filter(s=>s.responded_at).length > 0
    ? surveys.filter(s=>s.responded_at).reduce((sum,s)=>sum+(s.rating||0),0) / surveys.filter(s=>s.responded_at).length
    : null;

  return (
    <Box>
      {msg && <Alert severity="info" sx={{ mb:2, borderRadius:2 }} onClose={()=>setMsg("")}>{msg}</Alert>}

      {/* QR Code Section */}
      <Paper elevation={0} sx={{ p:2.5, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:2.5 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
          <QrCode2Icon sx={{ color:"#2f63ad" }} />
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>QR Code</Typography>
        </Box>
        <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
          Generate a QR code for this violation. Members can scan it to check the violation status without logging in.
          Scans are tracked automatically.
        </Typography>
        {loadingQR ? <CircularProgress size={20} /> : qrData ? (
          <Box>
            <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:1.5 }}>
              <Box sx={{ p:1.5, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)" }}>
                <Typography variant="caption" sx={{ color:"#6b778c", display:"block" }}>Status URL</Typography>
                <Typography variant="body2" sx={{ color:"#2f63ad", wordBreak:"break-all", maxWidth:300 }}>{qrUrl}</Typography>
              </Box>
              <Box sx={{ display:"flex", gap:2 }}>
                <Box sx={{ textAlign:"center" }}>
                  <Typography variant="h5" sx={{ fontWeight:800, color:"#223142" }}>{qrData.scan_count||0}</Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>Total Scans</Typography>
                </Box>
                {qrData.last_scanned_at && (
                  <Box sx={{ textAlign:"center" }}>
                    <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>
                      {new Date(qrData.last_scanned_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color:"#6b778c" }}>Last Scan</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box ref={qrCanvasRef} sx={{ display:"inline-block", p:1.5, bgcolor:"#fff", borderRadius:"12px", border:"1px solid rgba(47,99,173,0.12)", mb:1.5 }}>
              <QRCodeCanvas
                value={qrUrl}
                size={160}
                includeMargin
                {...(logoUrl ? {
                  imageSettings: {
                    src: logoUrl,
                    height: 32,
                    width: 32,
                    excavate: true,
                  }
                } : {})}
              />
            </Box>
            <Box>
              <Button size="small" variant="outlined" onClick={downloadQR}
                startIcon={<DownloadIcon />} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>
                Download QR Code
              </Button>
            </Box>
          </Box>
        ) : (
          <Button variant="contained" startIcon={<QrCode2Icon />} onClick={handleGenQR} disabled={genQR}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>
            {genQR ? "Generating..." : "Generate QR Code"}
          </Button>
        )}
      </Paper>

      {/* Survey Section */}
      <Paper elevation={0} sx={{ p:2.5, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
          <SurveyIcon sx={{ color:"#7c3aed" }} />
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Member Satisfaction Survey</Typography>
        </Box>
        <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
          Send a 1-question satisfaction survey to the member after resolving this violation.
          Must be enabled in Violations Manager → Advanced → Optional Features.
        </Typography>

        {avgRating !== null && (
          <Box sx={{ mb:2, p:1.5, borderRadius:"10px", bgcolor:"rgba(124,58,237,0.04)", border:"1px solid rgba(124,58,237,0.15)" }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#7c3aed", mb:0.5 }}>Average Rating</Typography>
            <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
              <Rating value={avgRating} precision={0.5} readOnly size="small" />
              <Typography variant="body2" sx={{ color:"#374151", fontWeight:700 }}>{avgRating.toFixed(1)}/5</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>({surveys.filter(s=>s.responded_at).length} response{surveys.filter(s=>s.responded_at).length!==1?"s":""})</Typography>
            </Box>
          </Box>
        )}

        <Button variant="contained" startIcon={<SurveyIcon />} onClick={handleSendSurvey} disabled={sendSv}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#7c3aed", boxShadow:"none", "&:hover":{ bgcolor:"#6d28d9" }, mb:2 }}>
          {sendSv ? "Creating Survey..." : "Create & Send Survey"}
        </Button>

        {!loadingSv && surveys.length > 0 && (
          <Box>
            <Divider sx={{ mb:1.5 }} />
            <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", textTransform:"uppercase", letterSpacing:0.6, display:"block", mb:1 }}>
              Survey History ({surveys.length})
            </Typography>
            {surveys.map(s => (
              <Box key={s.id} sx={{ mb:1, p:1.25, borderRadius:"8px", bgcolor: s.responded_at ? "rgba(124,58,237,0.04)" : "rgba(0,0,0,0.02)", border:"1px solid rgba(0,0,0,0.06)" }}>
                <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>Sent: {new Date(s.sent_at).toLocaleDateString()}</Typography>
                  <Chip size="small" label={s.responded_at ? "Responded" : "Pending"}
                    sx={{ fontWeight:700, fontSize:10, borderRadius:"6px", bgcolor: s.responded_at ? "rgba(34,197,94,0.1)" : "rgba(0,0,0,0.06)", color: s.responded_at ? "#16a34a" : "#6b778c" }} />
                </Box>
                {s.rating && (
                  <Box sx={{ mt:0.5 }}>
                    <Rating value={s.rating} readOnly size="small" />
                    {s.comment && <Typography variant="caption" sx={{ display:"block", color:"#374151", fontStyle:"italic" }}>"{s.comment}"</Typography>}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ViolationQRSurveyTab;
